import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AssigneesPicker from "@/components/AssigneesPicker";
import { useAuth } from "@/context/AuthContext";
import { UsersThree, FloppyDisk } from "@phosphor-icons/react";

/** Multi-role attribution card for a chantier — visible to admin/associate/compta only. */
export default function AssigneesSection({ chantier, onChanged }) {
  const { isAdmin } = useAuth();
  const [team, setTeam] = useState([]);
  const [value, setValue] = useState(chantier?.assignee_ids || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(chantier?.assignee_ids || []); }, [chantier?.id, chantier?.assignee_ids]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/users");
        setTeam((r.data || []).filter((u) => u.is_active !== false));
      } catch (e) { console.debug("[assignees] load team failed:", e?.message || e); }
    })();
  }, []);

  const dirty = JSON.stringify([...(value || [])].sort()) !== JSON.stringify([...(chantier?.assignee_ids || [])].sort());

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/chantiers/${chantier.id}`, { assignee_ids: value });
      toast.success("Attribution mise à jour");
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  // Read-only display when not admin
  const teamById = Object.fromEntries(team.map((u) => [u.id, u]));
  const assigned = (chantier?.assignee_ids || []).map((id) => teamById[id]).filter(Boolean);

  if (!isAdmin) {
    if (assigned.length === 0) return null;
    return (
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4" data-testid="assignees-section-ro">
        <div className="flex items-center gap-1.5 text-white/60 text-[11px] uppercase tracking-widest mb-2">
          <UsersThree size={12} weight="duotone" /> Équipe attribuée
        </div>
        <div className="flex flex-wrap gap-1.5">
          {assigned.map((u) => (
            <span key={u.id} className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/80">
              {u.name}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4" data-testid="assignees-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5 text-white/60 text-[11px] uppercase tracking-widest">
          <UsersThree size={12} weight="duotone" /> Attribution (Tech / Associé / Compta)
        </div>
        {dirty && (
          <button
            onClick={save}
            disabled={saving}
            className="text-[11px] px-2.5 py-1 rounded-full bg-[#005CFF] hover:bg-[#0047CC] text-white flex items-center gap-1 active:scale-95"
            data-testid="assignees-save"
          >
            <FloppyDisk size={11} weight="bold" /> {saving ? "…" : "Enregistrer"}
          </button>
        )}
      </div>
      <AssigneesPicker
        team={team}
        value={value}
        onChange={setValue}
        testid="chantier-assignees"
      />
      {(value || []).length === 0 && (
        <div className="text-[10px] text-white/40 mt-2 italic">Aucun équipier attribué</div>
      )}
    </div>
  );
}
